import { calculateArea, totalDistance } from "@nova/calculate-geospatial"
import { formatArea, formatDistance } from "@nova/format"
import { isUsable } from "@nova/constants"
import { InfraredFlight, InfraredImage } from "@nova/protobuf"
import {
    selectWorkspaceAreaUnit,
    selectWorkspaceDistanceUnit,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectFilteredHotspotsByFlightId } from "features/entity-caching/hotspots/selectors"
import { selectInfraredImagesAndUploadsByFlightId } from "features/entity-caching/images/hotspots/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"

export const useInspectorPanelHotspots = (entity: InfraredFlight | undefined) => {
    const filteredHotspots = useAppSelector(selectFilteredHotspotsByFlightId(entity?.id || ""))
    const uploads = useAppSelector(selectInfraredImagesAndUploadsByFlightId(entity?.id || ""))

    const areaUnit = useAppSelector(selectWorkspaceAreaUnit)
    const organizationSettingDistance = useAppSelector(selectWorkspaceDistanceUnit)

    const flightPathDistance = useMemo(() => {
        if (!uploads) return undefined
        const usableUploads = uploads.reduce((acc, cur) => {
            const upload = isUsable(cur)
            if (
                upload &&
                upload.takenAt &&
                upload.longitude !== undefined &&
                upload.latitude !== undefined
            )
                acc.push(upload as InfraredImage)
            return acc
        }, [] as InfraredImage[])

        const distance = totalDistance(
            usableUploads
                .sort((a, b) => (a.takenAt || 0) - (b.takenAt || 0))
                .map((image) => ({ longitude: image.longitude, latitude: image.latitude }))
        )
        const formattedDistance = formatDistance({
            value: distance,
            unit: organizationSettingDistance,
        })

        return formattedDistance
    }, [organizationSettingDistance, uploads])

    const formattedOrthoArea = useMemo(
        () =>
            entity?.job?.area ? formatArea({ value: entity.job.area, unit: areaUnit }) : undefined,
        [areaUnit, entity?.job?.area]
    )

    const formattedPolygonsArea = useMemo(() => {
        const area = filteredHotspots.reduce((area, hotspot) => {
            if (hotspot.polygon.length >= 3) area += calculateArea(hotspot.polygon)
            return area
        }, 0)

        return area ? formatArea({ value: area, unit: areaUnit, waterfall: true }) : undefined
    }, [filteredHotspots, areaUnit])

    return { flightPathDistance, formattedOrthoArea, formattedPolygonsArea }
}
